/**
 * scan-graph.js
 * Generic animated SVG line chart custom element.
 *
 * Supported data attributes:
 *   data-line1 / line2 / line3 / line4   JSON number arrays (all same length)
 *   data-line1-color … line4-color        Hex fill colors
 *   data-line1-label … line4-label        Legend strings
 *   data-line1-end-label … line4-end-label  Value labels at each line's right endpoint
 *   data-y-max                            Y-axis maximum
 *   data-y-min                            Optional Y-axis minimum; defaults to 0, or to the
 *                                         lowest data value floored to a step when negative
 *   data-y-step                           Optional Y gridline interval (overrides auto)
 *   data-y-label                          Y-axis title
 *   data-x-label                          X-axis caption (numeric mode only)
 *   data-x-labels                         JSON string array for custom X tick labels
 *   data-x-grid                           "true" → draw vertical gridlines
 *   data-fill-label-override              Wins over the data-fill-label the PDP snippet
 *                                         injects from factoid_title. Set it empty to
 *                                         suppress the right-side fill label entirely.
 *   data-stat-value                       Big callout number, e.g. "653%"
 *   data-stat-text                        Callout subtitle line 1 (normal weight)
 *   data-stat-line2                       Callout subtitle line 2 (bold weight)
 *
 * ViewBox: 0 0 700 450
 * Plot area: left=60, top=56, right=680, bottom=400
 * Legend row 1: y=16  (primary series)
 * Legend row 2: y=36  (secondary/tertiary/quaternary, when 3+ series)
 */

class ScanGraph extends HTMLElement {
  connectedCallback() {
    this.d1    = JSON.parse(this.dataset.line1    || '[]');
    this.d2    = JSON.parse(this.dataset.line2    || '[]');
    this.d3    = this.dataset.line3 ? JSON.parse(this.dataset.line3) : null;
    this.d4    = this.dataset.line4 ? JSON.parse(this.dataset.line4) : null;
    this.c1    = this.dataset.line1Color          || '#2196F3';
    this.c2    = this.dataset.line2Color          || '#aaaaaa';
    this.c3    = this.dataset.line3Color          || '#888888';
    this.c4    = this.dataset.line4Color          || '#bbbbbb';
    this.lbl1  = this.dataset.line1Label          || '';
    this.lbl2  = this.dataset.line2Label          || '';
    this.lbl3  = this.dataset.line3Label          || '';
    this.lbl4  = this.dataset.line4Label          || '';
    this.yMax  = parseFloat(this.dataset.yMax)  || 500;
    this.yStep = this.dataset.yStep ? parseFloat(this.dataset.yStep) : null;
    this.yMinAttr = this.dataset.yMin !== undefined ? parseFloat(this.dataset.yMin) : null;
    this.yLbl  = this.dataset.yLabel              || '';
    this.xLbl  = this.dataset.xLabel              || '';
    this.xLbls = this.dataset.xLabels ? JSON.parse(this.dataset.xLabels) : null;
    this.xGrid = this.dataset.xGrid === 'true';
    this.statV    = this.dataset.statValue          || '';
    this.statT    = this.dataset.statText           || '';
    this.statL2   = this.dataset.statLine2          || '';
    this._readLabels();

    this.svg        = this.querySelector('svg');
    this.done       = false;
    this._textScale = 1;

    if (!this.svg || !this.d1.length || !this.d2.length) return;

    this.yMin = this._resolveYMin();

    this._render();

    // One-shot ResizeObserver: recalculate text scale once the SVG has laid out
    const ro = new ResizeObserver(entries => {
      const w = entries[0].contentRect.width;
      if (!w) return;
      ro.disconnect();
      const ts = Math.max(1, Math.min(1.35, 520 / w));
      if (Math.abs(ts - this._textScale) < 0.08) return;
      this._textScale = ts;
      const wasDone = this.done;
      this._render();
      if (wasDone) { this._finish(); this.done = true; }
    });
    ro.observe(this.svg);
  }

  // ─── Re-draw and re-arm observers ─────────────────────────────────────────
  _readLabels() {
    const override = this.dataset.fillLabelOverride;
    this.fillColor     = this.dataset.fillColor || '';
    this.fillLabel     = override !== undefined ? override : (this.dataset.fillLabel || '');
    this.line1EndLabel = this.dataset.line1EndLabel || '';
    this.line2EndLabel = this.dataset.line2EndLabel || '';
    this.line3EndLabel = this.dataset.line3EndLabel || '';
    this.line4EndLabel = this.dataset.line4EndLabel || '';
  }

  _render() {
    this.done = false;
    this._readLabels();
    this.svg.replaceChildren();
    this._draw();
    if (this.closest('swiper-slide')?.classList.contains('swiper-slide-duplicate')) {
      this._finish();
    } else {
      this._observe();
    }
  }

  // ─── Coordinate helpers (ViewBox 0 0 700 450, plot l=60 t=56 r=680 b=400) ──
  _resolveYMin() {
    if (this.yMinAttr !== null && !Number.isNaN(this.yMinAttr)) return this.yMinAttr;
    const all = [this.d1, this.d2, this.d3, this.d4].filter(Boolean).flat();
    const min = Math.min(0, ...all);
    if (min >= 0) return 0;
    const step = this._gridStep();
    return Math.floor(min / step) * step;
  }

  _yTicks() {
    const step = this._gridStep();
    const out = [];
    const n = Math.round((this.yMax - this.yMin) / step);
    for (let i = 0; i <= n; i++) out.push(this.yMin + i * step);
    return out;
  }

  _cx(i) { return 60 + (i / (this.d1.length - 1)) * 620; }
  _cy(v) { return 400 - ((v - this.yMin) / (this.yMax - this.yMin)) * 344; }
  _pts(d) { return d.map((v, i) => `${this._cx(i).toFixed(1)},${this._cy(v).toFixed(1)}`).join(' '); }

  // ─── SVG element helpers ───────────────────────────────────────────────────
  _el(tag, attrs) {
    const el = document.createElementNS('http://www.w3.org/2000/svg', tag);
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
    return el;
  }

  _txt(content, attrs) {
    const el = this._el('text', attrs);
    el.textContent = content;
    return el;
  }

  _measure(str, fs, weight, font) {
    if (!str) return 0;
    const el = this._txt(str, {
      'font-size': fs, 'font-weight': weight, 'font-family': font, visibility: 'hidden',
    });
    this.svg.append(el);
    let w = 0;
    try { w = el.getComputedTextLength(); } catch (e) { w = 0; }
    el.remove();
    return w > 0 ? w : String(str).length * fs * 0.58;
  }

  // "Nice" Y gridline step — uses data-y-step if provided, otherwise auto
  _gridStep() {
    if (this.yStep) return this.yStep;
    const raw = this.yMax / 5;
    if (raw <= 0) return 1;
    const mag = Math.pow(10, Math.floor(Math.log10(raw)));
    const n   = raw / mag;
    const nice = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
    return nice * mag;
  }

  // ─── Draw all SVG elements ─────────────────────────────────────────────────
  _draw() {
    const s    = this.svg;
    const n    = this.d1.length - 1;
    const secondaryEnds = [
      { txt: this.line2EndLabel, data: this.d2, color: this.c2 },
      { txt: this.line3EndLabel, data: this.d3, color: this.c3 },
      { txt: this.line4EndLabel, data: this.d4, color: this.c4 },
    ].filter(e => e.txt && e.data && e.data[n] !== undefined);
    const hasEndAnnotations = !!(this.line1EndLabel || secondaryEnds.length || this.fillLabel);
    const font = 'system-ui,sans-serif';
    const multiSeries = !!this.d3; // 3+ series → two-row legend

    // Text scale derived from ResizeObserver measurement
    const ts = this._textScale;
    const fs = Math.round(14 * ts);   // standard font size
    const r1 = Math.round(9  * ts);   // primary dot radius
    const r2 = Math.round(5  * ts);   // secondary dot radius

    // Expand viewBox to give x-axis labels more headroom; extra width for fill label
    let vbW = 700;
    if (this.fillLabel) {
      const fillFs = Math.round(16 * ts);
      const need = 680 + Math.round(6 * ts) + this._measure(this.fillLabel, fillFs, 700, font) + 8;
      vbW = Math.max(728, Math.ceil(need));
    }
    const endFs = Math.round(12 * ts);
    [
      { txt: this.line1EndLabel, size: fs, weight: 700 },
      ...secondaryEnds.map(e => ({ txt: e.txt, size: endFs, weight: 400 })),
    ].forEach(({ txt, size, weight }) => {
      if (!txt) return;
      vbW = Math.max(vbW, Math.ceil(680 + this._measure(txt, size, weight, font) / 2 + 4));
    });
    s.setAttribute('viewBox', `0 0 ${vbW} 462`);

    // ── Legend ───────────────────────────────────────────────────────────────
    if (multiSeries) {
      // Row 1: primary series (large dot)
      s.append(this._el('circle', { cx: 60, cy: 16, r: r1, fill: this.c1 }));
      s.append(this._txt(this.lbl1, {
        x: 60 + r1 + 9, y: 16, 'dominant-baseline': 'middle',
        'font-size': fs, 'font-weight': 600, fill: '#374151', 'font-family': font,
      }));
      // Row 2: comparison series (small dots)
      const row2 = [
        { cx: 60,  color: this.c2, lbl: this.lbl2 },
        { cx: 260, color: this.c3, lbl: this.lbl3 },
        ...(this.d4 ? [{ cx: 450, color: this.c4, lbl: this.lbl4 }] : []),
      ];
      row2.forEach(({ cx, color, lbl }) => {
        s.append(this._el('circle', { cx, cy: 36, r: r2, fill: color }));
        s.append(this._txt(lbl, {
          x: cx + r2 + 8, y: 36, 'dominant-baseline': 'middle',
          'font-size': fs, fill: '#6b7280', 'font-family': font,
        }));
      });
    } else {
      // Single row: primary + one secondary
      s.append(this._el('circle', { cx: 60, cy: 22, r: r1, fill: this.c1 }));
      s.append(this._txt(this.lbl1, {
        x: 60 + r1 + 9, y: 22, 'dominant-baseline': 'middle',
        'font-size': fs, 'font-weight': 600, fill: '#374151', 'font-family': font,
      }));
      s.append(this._el('circle', { cx: 290, cy: 22, r: r2, fill: this.c2 }));
      s.append(this._txt(this.lbl2, {
        x: 290 + r2 + 8, y: 22, 'dominant-baseline': 'middle',
        'font-size': fs, fill: '#6b7280', 'font-family': font,
      }));
    }

    // ── Y-axis title (rotated) ────────────────────────────────────────────────
    const yMid = (56 + 400) / 2;
    s.append(this._txt(this.yLbl.toUpperCase(), {
      x: 12, y: yMid, 'text-anchor': 'middle',
      transform: `rotate(-90,12,${yMid})`,
      'font-size': fs, fill: '#9ca3af', 'letter-spacing': 1.5, 'font-family': font,
    }));

    // ── Horizontal grid lines + Y labels ─────────────────────────────────────
    this._yTicks().forEach((v) => {
      const yp = this._cy(v);
      const isZero = v === 0 && this.yMin < 0;
      s.append(this._el('line', {
        x1: 60, y1: yp, x2: 680, y2: yp,
        stroke: isZero ? '#9ca3af' : '#e5e7eb', 'stroke-width': isZero ? 1.5 : 1,
      }));
      s.append(this._txt(v, {
        x: 52, y: yp, 'text-anchor': 'end', 'dominant-baseline': 'middle',
        'font-size': fs, 'font-weight': 600, fill: '#6b7280', 'font-family': font,
      }));
    });

    // ── Vertical grid lines (optional) ───────────────────────────────────────
    if (this.xGrid) {
      for (let i = 0; i <= n; i++) {
        s.append(this._el('line', {
          x1: this._cx(i).toFixed(1), y1: 56,
          x2: this._cx(i).toFixed(1), y2: 400,
          stroke: '#e5e7eb', 'stroke-width': 1,
        }));
      }
    }

    // ── X labels ─────────────────────────────────────────────────────────────
    if (this.xLbls) {
      this.xLbls.forEach((lbl, i) => {
        if (!lbl) return;
        s.append(this._txt(lbl, {
          x: this._cx(i).toFixed(1), y: 430, 'text-anchor': 'middle',
          'font-size': fs, fill: '#9ca3af', 'letter-spacing': 1.5, 'font-family': font,
        }));
      });
    } else {
      for (let i = 1; i <= n; i++) {
        s.append(this._txt(i, {
          x: this._cx(i).toFixed(1), y: 430, 'text-anchor': 'middle',
          'font-size': fs, 'font-weight': 600, fill: '#6b7280', 'font-family': font,
        }));
      }
    }
    if (this.xLbl) {
      s.append(this._txt(this.xLbl.toUpperCase(), {
        x: 60, y: 452, 'text-anchor': 'start',
        'font-size': fs, fill: '#9ca3af', 'letter-spacing': 1.5, 'font-family': font,
      }));
    }

    // ── Extra comparison lines (drawn bottom-up so primary is on top) ─────────
    [
      { data: this.d4, color: this.c4 },
      { data: this.d3, color: this.c3 },
    ].forEach(({ data, color }) => {
      if (!data) return;
      s.append(this._el('polyline', {
        points: this._pts(data),
        fill: 'none', stroke: color, 'stroke-width': 2,
        'stroke-dasharray': '7 5', 'stroke-linecap': 'round',
      }));
      data.forEach((v, i) => {
        s.append(this._el('circle', {
          cx: this._cx(i).toFixed(1), cy: this._cy(v).toFixed(1),
          r: r2, fill: color, stroke: 'white', 'stroke-width': 1.5,
        }));
      });
    });

    // ── Secondary line (dashed, static) ──────────────────────────────────────
    s.append(this._el('polyline', {
      points: this._pts(this.d2),
      fill: 'none', stroke: this.c2, 'stroke-width': 2,
      'stroke-dasharray': '7 5', 'stroke-linecap': 'round',
    }));
    this._dots2 = this.d2.map((v, i) => {
      const c = this._el('circle', {
        cx: this._cx(i).toFixed(1), cy: this._cy(v).toFixed(1),
        r: r2, fill: this.c2, stroke: 'white', 'stroke-width': 1.5,
      });
      s.append(c);
      return c;
    });

    // ── Area fill between d1 and d2 (optional, starts collapsed at d2) ───────
    if (this.fillColor) {
      const fwdPts = this.d2.map((v, i) => `${this._cx(i).toFixed(1)},${this._cy(v).toFixed(1)}`);
      const bakPts = [...this.d2].reverse().map((v, i) => `${this._cx(this.d2.length - 1 - i).toFixed(1)},${this._cy(v).toFixed(1)}`);
      this._fillPoly = this._el('polygon', {
        points: [...fwdPts, ...bakPts].join(' '),
        fill: this.fillColor,
      });
      s.append(this._fillPoly);
    } else {
      this._fillPoly = null;
    }

    // ── Endpoint connector + labels ───────────────────────────────────────────
    if (hasEndAnnotations) {
      const lx  = this._cx(n);
      const ly1 = this._cy(this.d1[n]);
      const ly2 = this._cy(this.d2[n]);

      s.append(this._el('line', {
        x1: lx.toFixed(1), y1: ly1.toFixed(1),
        x2: lx.toFixed(1), y2: ly2.toFixed(1),
        stroke: this.c2, 'stroke-width': 1.5,
        'stroke-dasharray': '5 4', 'stroke-linecap': 'round',
      }));

      this._line1EndLabelEl = null;
      this._fillLabelEl = null;

      if (this.line1EndLabel) {
        this._line1EndLabelEl = this._txt(this.line1EndLabel, {
          x: lx, y: ly1 - Math.round(18 * ts), 'text-anchor': 'middle',
          'font-size': fs, 'font-weight': 700, fill: this.c1, 'font-family': font, opacity: 0,
        });
        s.append(this._line1EndLabelEl);
      }

      const minGap = Math.round(15 * ts);
      secondaryEnds
        .map(e => ({ ...e, y: this._cy(e.data[n]) + Math.round(26 * ts) }))
        .sort((a, b) => a.y - b.y)
        .forEach((e, i, arr) => {
          if (i > 0 && e.y - arr[i - 1].y < minGap) e.y = arr[i - 1].y + minGap;
          s.append(this._txt(e.txt, {
            x: lx, y: e.y, 'text-anchor': 'middle',
            'font-size': endFs, fill: e.color, 'font-family': font,
          }));
        });

      if (this.fillLabel) {
        this._fillLabelEl = this._txt(this.fillLabel, {
          x: lx + Math.round(6 * ts), y: (ly1 + ly2) / 2,
          'text-anchor': 'start', 'dominant-baseline': 'middle',
          'font-size': Math.round(16 * ts), 'font-weight': 700, fill: this.c1, 'font-family': font, opacity: 0,
        });
        s.append(this._fillLabelEl);
      }
    }

    // ── Primary line — starts at d2 positions, animates to d1 ────────────────
    this._poly1 = this._el('polyline', {
      points: this._pts(this.d2),
      fill: 'none', stroke: this.c1, 'stroke-width': 3,
      'stroke-linecap': 'round', 'stroke-linejoin': 'round',
    });
    s.append(this._poly1);

    this._dots1 = this.d1.map((_, i) => {
      const c = this._el('circle', {
        cx: this._cx(i).toFixed(1),
        cy: this._cy(this.d2[i] ?? 0).toFixed(1),
        r: r1, fill: this.c1, stroke: 'white', 'stroke-width': 2.5,
      });
      s.append(c);
      return c;
    });

    // ── Stat callout box (optional, always on top) ────────────────────────────
    if (this.statV) this._drawStat(font);
  }

  // ─── Stat callout (top-right of chart area) ────────────────────────────────
  _drawStat(font) {
    const s = this.svg;
    const hasLine2 = !!this.statL2;
    const bx = 505, by = 56, bw = 175, bh = hasLine2 ? 116 : 100;

    s.append(this._el('rect', {
      x: bx, y: by, width: bw, height: bh, rx: 12,
      fill: 'white', stroke: '#e5e7eb', 'stroke-width': 1.5,
    }));

    // Big number
    s.append(this._txt(this.statV, {
      x: bx + 14, y: by + 62,
      'font-size': 48, 'font-weight': 900, fill: '#111827', 'font-family': font,
    }));

    // Trend icon
    const ix = bx + bw - 38, iy = by + 14;
    s.append(this._el('polyline', {
      points: `${ix},${iy + 26} ${ix + 10},${iy + 14} ${ix + 18},${iy + 18} ${ix + 28},${iy}`,
      fill: 'none', stroke: this.c1, 'stroke-width': 2,
      'stroke-linecap': 'round', 'stroke-linejoin': 'round',
    }));
    s.append(this._el('circle', { cx: ix + 28, cy: iy, r: 2.5, fill: this.c1 }));

    // Subtitle line 1
    if (this.statT) {
      s.append(this._txt(this.statT, {
        x: bx + 14, y: by + (hasLine2 ? 82 : 84),
        'font-size': 12, fill: '#374151', 'font-family': font,
      }));
    }

    // Subtitle line 2 (bold)
    if (hasLine2) {
      s.append(this._txt(this.statL2, {
        x: bx + 14, y: by + 98,
        'font-size': 12, 'font-weight': 700, fill: '#374151', 'font-family': font,
      }));
    }
  }

  // ─── Intersection observer ─────────────────────────────────────────────────
  _observe() {
    const target = this.closest('swiper-slide') || this;
    const slideIndex = parseInt(target.dataset.swiperSlideIndex ?? target.dataset.index ?? 0, 10) || 0;
    const stagger = slideIndex * 150;
    const io = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting || this.done) return;
      this.done = true;
      io.disconnect();
      setTimeout(() => this._animate(), 350 + stagger);
    }, { threshold: 0.9 });
    io.observe(target);
  }

  // ─── Snap primary to final positions (used by Swiper duplicate slides) ─────
  _finish() {
    this._poly1.setAttribute('points', this._pts(this.d1));
    this._dots1.forEach((dot, i) => {
      dot.setAttribute('cy', this._cy(this.d1[i]).toFixed(2));
    });
    if (this._fillPoly) {
      const fwdPts = this.d1.map((v, i) => `${this._cx(i).toFixed(1)},${this._cy(v).toFixed(1)}`);
      const bakPts = [...this.d2].reverse().map((v, i) => `${this._cx(this.d2.length - 1 - i).toFixed(1)},${this._cy(v).toFixed(1)}`);
      this._fillPoly.setAttribute('points', [...fwdPts, ...bakPts].join(' '));
    }
    [this._line1EndLabelEl, this._fillLabelEl].forEach(el => {
      if (el) el.style.opacity = '1';
    });
  }

  // ─── Animation: interpolate primary from secondary → actual ───────────────
  _animate() {
    const dur   = 1100;
    const start = performance.now();
    const ease  = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

    const tick = (now) => {
      const p = ease(Math.min((now - start) / dur, 1));

      const animPts = this.d1.map((v, i) => {
        const y0 = this._cy(this.d2[i] ?? 0);
        const y1 = this._cy(v);
        return `${this._cx(i).toFixed(1)},${(y0 + (y1 - y0) * p).toFixed(2)}`;
      });

      this._poly1.setAttribute('points', animPts.join(' '));

      if (this._fillPoly) {
        const bakPts = [...this.d2].reverse().map((v, i) => `${this._cx(this.d2.length - 1 - i).toFixed(1)},${this._cy(v).toFixed(1)}`);
        this._fillPoly.setAttribute('points', [...animPts, ...bakPts].join(' '));
      }

      this._dots1.forEach((dot, i) => {
        const y0 = this._cy(this.d2[i] ?? 0);
        const y1 = this._cy(this.d1[i]);
        dot.setAttribute('cy', (y0 + (y1 - y0) * p).toFixed(2));
      });

      if (p < 1) {
        requestAnimationFrame(tick);
      } else {
        [this._line1EndLabelEl, this._fillLabelEl].forEach(el => {
          if (!el) return;
          el.style.transition = 'opacity 0.5s ease';
          el.style.opacity = '1';
        });
      }
    };

    requestAnimationFrame(tick);
  }
}

if (!customElements.get('scan-graph')) {
  customElements.define('scan-graph', ScanGraph);
}

// =============================================================================
// ScanBar — animated horizontal arrow-bar chart
//
// Supported data attributes:
//   data-bar1 / bar2              Numeric values (decimals supported)
//   data-bar1-color / bar2-color  Hex fill colors
//   data-bar1-label / bar2-label  Row label strings (auto-wraps; use | to force a break)
//   data-x-max                    X-axis maximum value (decimals supported)
//   data-x-step                   X-axis gridline / tick interval; tick decimal places
//                                 are derived from this and data-x-max
//   data-x-label                  Bottom caption
//   data-x-format                 "K" → divide by 1000 and append "K"
//
// ViewBox: 0 -80 700 462
// Label column: x=0–200 (labels wrap to fit)  |  Chart area: x=200–680 (480px)
// Strip 1: cy=70  |  Strip 2: cy=182  (BAR_H=72)
// =============================================================================

class ScanBar extends HTMLElement {
  connectedCallback() {
    this.v1   = parseFloat(this.dataset.bar1) || 0;
    this.v2   = parseFloat(this.dataset.bar2) || 0;
    this.c1   = this.dataset.bar1Color || '#2196F3';
    this.c2   = this.dataset.bar2Color || '#b0b0b0';
    this.lbl1 = this.dataset.bar1Label || '';
    this.lbl2 = this.dataset.bar2Label || '';
    this.xMax  = parseFloat(this.dataset.xMax)  || 30000;
    this.xStep = parseFloat(this.dataset.xStep) || 10000;
    this.xLbl  = this.dataset.xLabel  || '';
    this.xFmt  = this.dataset.xFormat || '';

    if (!(this.xStep > 0) || this.xStep > this.xMax) this.xStep = this.xMax / 4;
    this.xDec = this._decimals(this.xStep, this.xMax);

    this.svg        = this.querySelector('svg');
    this.done       = false;
    this._textScale = 1;

    if (!this.svg) return;

    this._render();

    const ro = new ResizeObserver(entries => {
      const w = entries[0].contentRect.width;
      if (!w) return;
      ro.disconnect();
      const ts = Math.max(1, Math.min(1.35, 520 / w));
      if (Math.abs(ts - this._textScale) < 0.08) return;
      this._textScale = ts;
      const wasDone = this.done;
      this._render();
      if (wasDone) { this._finish(); this.done = true; }
    });
    ro.observe(this.svg);
  }

  // ─── Re-draw and re-arm observers ─────────────────────────────────────────
  _render() {
    this.done = false;
    this.svg.replaceChildren();
    this._draw();
    if (this.closest('swiper-slide')?.classList.contains('swiper-slide-duplicate')) {
      this._finish();
    } else {
      this._observe();
    }
  }

  get _labelW() { return Math.round(200 * this._textScale); }

  _xpos(v) { return this._labelW + (v / this.xMax) * (680 - this._labelW); }

  _decimals(...vals) {
    return vals.reduce((max, v) => {
      const str = String(v);
      const dot = str.indexOf('.');
      return Math.max(max, dot === -1 ? 0 : str.length - dot - 1);
    }, 0);
  }

  _ticks() {
    const out = [];
    const n = Math.floor(this.xMax / this.xStep + 1e-9);
    for (let i = 0; i <= n; i++) out.push(i * this.xStep);
    return out;
  }

  _fmt(v) {
    if (this.xFmt === 'K') return (v / 1000) + 'K';
    return v.toFixed(this.xDec);
  }

  _el(tag, attrs) {
    const el = document.createElementNS('http://www.w3.org/2000/svg', tag);
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
    return el;
  }

  _txt(content, attrs) {
    const el = this._el('text', attrs);
    el.textContent = content;
    return el;
  }

  _barPath(x0, x1, cy, h, tip) {
    const top = cy - h / 2, bot = cy + h / 2;
    if (x1 - x0 < tip) {
      return `M${x0},${top} L${x1},${top} L${x1},${bot} L${x0},${bot} Z`;
    }
    return `M${x0},${top} L${(x1 - tip).toFixed(1)},${top} L${x1.toFixed(1)},${cy} L${(x1 - tip).toFixed(1)},${bot} L${x0},${bot} Z`;
  }

  _measure(str, fs, font) {
    if (!str) return 0;
    const el = this._txt(str, { 'font-size': fs, 'font-family': font, visibility: 'hidden' });
    this.svg.append(el);
    let w = 0;
    try { w = el.getComputedTextLength(); } catch (e) { w = 0; }
    el.remove();
    return w > 0 ? w : str.length * fs * 0.55;
  }

  _lines(label, maxW, fs, font, maxLines) {
    if (label.includes('|')) return label.split('|').map(p => p.trim());
    if (this._measure(label, fs, font) <= maxW) return [label];

    const words = label.split(/\s+/).filter(Boolean);
    const lines = [];
    let current = '';

    words.forEach((word) => {
      const next = current ? `${current} ${word}` : word;
      if (current && this._measure(next, fs, font) > maxW && lines.length < maxLines - 1) {
        lines.push(current);
        current = word;
      } else {
        current = next;
      }
    });
    if (current) lines.push(current);
    return lines;
  }

  _drawLabel(input, x, cy, font, fs) {
    const s = this.svg;
    const lines = Array.isArray(input) ? input : String(input).split('|').map(p => p.trim());
    if (lines.length === 1) {
      s.append(this._txt(lines[0], {
        x, y: cy, 'dominant-baseline': 'middle',
        'font-size': fs, fill: '#374151', 'font-family': font,
      }));
      return;
    }

    const lh = Math.round(fs * 1.25);
    const el = this._el('text', {
      x, y: cy - ((lines.length - 1) * lh) / 2, 'dominant-baseline': 'middle',
      'font-size': fs, fill: '#374151', 'font-family': font,
    });
    lines.forEach((part, i) => {
      const ts = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
      ts.textContent = part;
      ts.setAttribute('x', x);
      if (i > 0) ts.setAttribute('dy', lh);
      el.appendChild(ts);
    });
    s.append(el);
  }

  _draw() {
    const s    = this.svg;
    const font = 'system-ui,sans-serif';
    const BAR_H = 72, TIP = 22;

    const ts = this._textScale;
    const fs = Math.round(14 * ts);

    const rows = [
      { cy: 70,  label: this.lbl1, color: this.c1, val: this.v1 },
      { cy: 182, label: this.lbl2, color: this.c2, val: this.v2 },
    ];

    s.setAttribute('viewBox', '0 -80 700 462');

    const PAD_L = 14, PAD_R = 14, MAX_LINES = 4;
    const labelW = this._labelW;
    const textMaxW = labelW - PAD_L - PAD_R;

    rows.forEach((row) => {
      row.lines = this._lines(row.label, textMaxW, fs, font, MAX_LINES);
    });

    rows.forEach(({ cy }) => {
      s.append(this._el('rect', {
        x: 0, y: cy - BAR_H / 2, width: labelW, height: BAR_H, fill: '#e7fdfc',
      }));
    });

    this._ticks().forEach((v) => {
      const x = this._xpos(v).toFixed(1);
      s.append(this._el('line', {
        x1: x, y1: 34, x2: x, y2: 218,
        stroke: '#c8dbd9', 'stroke-width': 1,
      }));
    });

    rows.forEach(({ cy, lines }) => {
      this._drawLabel(lines, PAD_L, cy, font, fs);
    });

    const x0 = this._xpos(0);

    s.append(this._el('path', {
      d: this._barPath(x0, this._xpos(this.v1), rows[0].cy, BAR_H, TIP),
      fill: this.c1,
    }));

    const xStart = this._xpos(this.v1);
    const bar2el = this._el('path', {
      d: this._barPath(x0, xStart, rows[1].cy, BAR_H, TIP),
      fill: this.c2,
    });
    s.append(bar2el);
    this._bar2 = { el: bar2el, x0, xStart, x1: this._xpos(this.v2), cy: rows[1].cy };

    this._ticks().forEach((v) => {
      s.append(this._txt(this._fmt(v), {
        x: this._xpos(v).toFixed(1), y: 238,
        'text-anchor': v === 0 ? 'start' : (v >= this.xMax ? 'end' : 'middle'),
        'font-size': fs, 'font-weight': 600, fill: '#6b7280', 'font-family': font,
      }));
    });

    if (this.xLbl) {
      s.append(this._txt(this.xLbl.toUpperCase(), {
        x: 14, y: 262, 'text-anchor': 'start',
        'font-size': fs, fill: '#9ca3af', 'letter-spacing': 1.5, 'font-family': font,
      }));
    }
  }

  _finish() {
    const { el, x0, x1, cy } = this._bar2;
    el.setAttribute('d', this._barPath(x0, x1, cy, 72, 22));
  }

  _observe() {
    const target = this.closest('swiper-slide') || this;
    const slideIndex = parseInt(target.dataset.swiperSlideIndex ?? target.dataset.index ?? 0, 10) || 0;
    const stagger = slideIndex * 150;
    const io = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting || this.done) return;
      this.done = true;
      io.disconnect();
      setTimeout(() => this._animate(), 350 + stagger);
    }, { threshold: 0.9 });
    io.observe(target);
  }

  _animate() {
    const dur   = 1100;
    const start = performance.now();
    const ease  = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

    const { el, x0, xStart, x1, cy } = this._bar2;
    const tick = (now) => {
      const p = ease(Math.min((now - start) / dur, 1));
      el.setAttribute('d', this._barPath(x0, xStart + (x1 - xStart) * p, cy, 72, 22));
      if (p < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }
}

if (!customElements.get('scan-bar')) {
  customElements.define('scan-bar', ScanBar);
}
